import { useMemo } from 'react';
import { useGlobalPluginStore } from '../store';
import { Plugin, pluginDisplayName } from '../registry';
import { ProgressEntry } from '../types/logs';

export function usePluginProgress(plugin?: string): ProgressEntry[] {
  return useGlobalPluginStore((s) =>
    plugin
      ? s.progress[plugin]
        ? [s.progress[plugin]!]
        : []
      : Object.values(s.progress),
  );
}

/** One row per active plugin, whether or not it reports progress. */
export interface PluginRow {
  plugin: Plugin | string;
  /** The plugin's display name, e.g. 'Parse Number'. */
  name: string;
  /** Reported percentage. Meaningless when `reports` is false. */
  value: number;
  message?: string;
  time: number;
  /**
   * Whether this plugin calls progress(). The synchronous ones never do — there
   * is no duration to measure — so their rows show a state instead of a bar.
   */
  reports: boolean;
  /**
   * Whether the plugin's bootstrap hook has resolved. This is *not* completion:
   * plugins whose work happens in postFieldSuggestions bootstrap instantly and
   * then do their real work later.
   */
  bootstrapped: boolean;
}

/**
 * The two halves of the store are keyed differently: `plugins` holds registry
 * keys ('anchorExtract') while `progress` is keyed by each plugin's display
 * name ('Anchor Extract'), because define.ts reports under `opts.name`. The
 * display name is only available once the plugin hook has been instantiated,
 * so match on a normalised form instead of trying to resolve it here.
 */
const normalise = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The progress store only holds plugins that called progress(), so a view built
 * from it alone hides every plugin doing synchronous work. This lists each
 * active plugin and attaches its reported progress where there is any.
 */
export function usePluginRows(): PluginRow[] {
  const plugins = useGlobalPluginStore((s) => s.plugins);
  const progress = useGlobalPluginStore((s) => s.progress);
  const bootstrapped = useGlobalPluginStore((s) => s.bootstrapped);

  return useMemo(() => {
    const reportedByName = new Map(
      Object.values(progress).map((entry) => [normalise(String(entry.plugin)), entry]),
    );

    return plugins.map((plugin) => {
      const hasBootstrapped = bootstrapped[plugin] === true;
      const reported = reportedByName.get(normalise(String(plugin)));
      const name = pluginDisplayName(String(plugin));

      if (reported) {
        return { ...reported, name, reports: true, bootstrapped: hasBootstrapped };
      }

      return {
        plugin,
        name,
        value: 0,
        time: 0,
        reports: false,
        bootstrapped: hasBootstrapped,
      };
    });
  }, [plugins, progress, bootstrapped]);
}

export function resetProgress(plugin?: string) {
  useGlobalPluginStore.getState().resetProgress(plugin);
}
